import { createContext, FC, useState, useEffect, useContext, useMemo } from "react"
import { InitMessage } from "../../libs/react-components/InitMessage"
import { readTokenFromLocalStorage } from "../../system/hooks/useToken"
import JwtBearerToken from "../../system/jwtBearerToken"
import jwtDecode from "jwt-decode"
import { DefaultUserInterface } from "../defaultUserInterface"
import { ContractorDataProvider } from "../../Services/DataProviders/ContractorDataProvider";
import { TimeZoneDataProvider } from "../../Services/DataProviders/TimeZoneDataProvider";
import { useAppContext } from "./appContextProvider"
import { SysOptionsDataProvider } from "../../Services/DataProviders/SysOptionsDataProvider"
import { SysOptionName } from "../../@types/enumsGlobal"
import { ISysOptionsDTO } from "../../libs/coreapi-dto/service/sysOptions"
import { UserDataProvider } from "../../Services/DataProviders/UserDataProvider"
import { DateTime } from "luxon"
import { CompanyDataProvider } from "../../Services/DataProviders/CompanyDataProvider"
import { useTokenData } from "../hooks/useTokenData"

export interface IUserContext {
    idGlobal: string
    userRoles: string[]
    userName: string
    licenseValidTo: DateTime
    userPermission: string[]
    userInterface: UserInterface.IUserInterface
    userTimeZoneOffset: string
    userTimeZoneId: string
    serverTimeZoneId: string
    updateTimeZoneOffset: () => void,
    isProAptekaEnabled: boolean | null,
    idPluginSettingTypeGlobal?: string
    // defaultViewListSettings: IBusinessPlugins
}

const defaultTimeZoneId = "Etc/UTC" //(UTC) Время в формате UTC
const defaultTimeZoneOffset = "00:00:00"

const defaultUserContext: IUserContext = {
    idGlobal: "",
    userRoles: [],
    userInterface: {} as UserInterface.IUserInterface,
    userName: "",
    licenseValidTo: DateTime.now(),
    userPermission: [],
    userTimeZoneOffset: defaultTimeZoneOffset,
    userTimeZoneId: defaultTimeZoneId,
    serverTimeZoneId: defaultTimeZoneId,
    updateTimeZoneOffset: () => { },
    isProAptekaEnabled: null,
    idPluginSettingTypeGlobal: ""
    // defaultViewListSettings: {
    //     idProfileGlobal: "",
    //     plugins: []
    // }
}

const UserContext = createContext<IUserContext>(defaultUserContext)

const UserContextProvider: FC = (props) => {
    const appContext = useAppContext()
    const userContext = useUserContext()
    const contractorDP = new ContractorDataProvider(appContext.coreApiService)
    const sysOptionsDP = new SysOptionsDataProvider(appContext.coreApiService)
    const companyDP = new CompanyDataProvider(appContext.coreApiService)
    const timezoneDP = new TimeZoneDataProvider(appContext.coreApiService)
    const userDP = new UserDataProvider(appContext.coreApiService)

    const [componentState, setComponentState] = useState<ComponentStateType>("Initializing")
    const [userInterface, setUserInterface] = useState<UserInterface.IUserInterface | null>(null)
    const token = readTokenFromLocalStorage() as JwtBearerToken;
    const jwtToken = localStorage.getItem("TokenData");
    const decodedToken: DecodedToken = jwtDecode(jwtToken as string);

    const defaultOffset = defaultTimeZoneOffset
    const [licenseValidTo, setLicenseValidTo] = useState<DateTime>()
    const [timeZoneOff, setTimeZoneOff] = useState<string>(defaultOffset)
    const [timeZoneId, setTimeZoneId] = useState<string>(defaultTimeZoneId)
    const [serverTimeZoneId, setServerTimeZoneId] = useState<string>(defaultTimeZoneId)
    const [isProAptekaEnabled, setIsProAptekaEnabled] = useState<boolean | null>(null);

    const { getRole } = useTokenData();
    const roleUser = getRole()

    const loadProAptekaSettings = (): Promise<boolean> => {
        return new Promise((resolve) => {
            
            sysOptionsDP.getByName(SysOptionName.ProAptekaEnabled, (e) => {
                try {
                    if (!e) {
                        console.warn("Настройка ProAptekaEnabled не найдена");
                        setIsProAptekaEnabled(false);
                        resolve(false);
                        return;
                    }

                    const data = e as ISysOptionsDTO;
                    const jsonData = JSON.parse(data.jsonData);
                    const settings = jsonData.Value;
                    
                    setIsProAptekaEnabled(settings);
                    resolve(settings);
                } catch (error) {
                    setIsProAptekaEnabled(false);
                    resolve(false);
                }
            });
        });
    };

    useEffect(() => {
        const init = async () => {
            try {
                const tokenPayload = token.getTokenPayload();

                const proAptekaSettings = await loadProAptekaSettings();
                const ui = await DefaultUserInterface(proAptekaSettings, roleUser);
                
                setUserInterface(ui);
                
                let userName = tokenPayload.name;
                if (userName !== 'admin'){
                    updateTimeZoneOffset()
                    setServerTimeZone()
                }
                setComponentState("Initialized");
            } catch (error) {
                setComponentState("Failed");
            }
        };
        
        init();
    }, [appContext.coreApiService]);

    function setTimeZoneOff_func(idTimeZone: string) {
        timezoneDP.GetTimeZoneOffset(idTimeZone, (e) => {
            if (e) {
                setTimeZoneOff(e.offsetTimeZone)
                setTimeZoneId(idTimeZone)
            }
        })
    }
    
    function setServerTimeZone() {
        timezoneDP.GetServerTimeZone((e) => {
            if (e) {
                setServerTimeZoneId(e)
            }
        })
    }

    function updateTimeZoneOffset() {
        contractorDP.firstMyForTimeZone((idTimeZone) => {
            //idTimeZone = "America/Anchorage"
            setTimeZoneOff_func(idTimeZone)
        })
    }

    // useEffect(() => {
    //     let userName = token.getTokenPayload().name;
    //     if (userName !== 'admin'){
    //         updateTimeZoneOffset()
    //         setServerTimeZone()
    //     }
    // }, [])
    function updateLicenseValidTo() {
        companyDP.getLicenseByCompanyId(decodedToken.company_id, (data) =>
            setLicenseValidTo(data.validTo)
        )
    }

    useEffect(() => {
        let userName = token.getTokenPayload().name;
        if (userName !== 'admin'){
            updateTimeZoneOffset()
            setServerTimeZone()
            updateLicenseValidTo()
        }
    }, [])

    // useEffect(()=>{
    //     appCtx.uiApiService.defaultViewListSettingsRequest.getSettings(e=>{
    //         if(e.respType==="isCompleted"){
    //             setViewListSettings({
    //                 idProfileGlobal:"default",
    //                 plugins:e.data.map(i=>{
    //                     return {
    //                         id:i.plugin.pluginId,
    //                         name: i.displayName,
    //                         mnemocode: i.plugin.mnemocode as IPlugin,
    //                         columns: i.viewListColumns.map(c=>{
    //                             return {
    //                                 order:c.order,
    //                                 propertyName: c.propertyName,
    //                                 propertyType: c.propertyType,
    //                                 displayName: c.displayName,
    //                                 visibility: c.isVisible,
    //                                 width: c.width
    //                             }
    //                         })
    //                     }
    //                 })
    //             })
    //             let timer = setTimeout(() => {
    //                 setComponentState("Initialized")
    //                 return clearTimeout(timer)
    //             }, 1500)
    //         }
    //         else{
    //             setComponentState("Failed")
    //         }
    //     })
    // },[])
    if (componentState === "Initializing" || !userInterface) {
        return (<InitMessage>Инициализация пользовательских данных.</InitMessage>)
    }
    else {
        return (
            <UserContext.Provider
                value={{
                    idGlobal: token.getTokenPayload().idGlobal,
                    userRoles: Array.isArray(decodedToken.role) ? decodedToken.role : decodedToken.role ? [decodedToken.role] : [],
                    userName: token.getTokenPayload().name,
                    userInterface: userInterface,
                    licenseValidTo: licenseValidTo ? licenseValidTo as DateTime : DateTime.now(),
                    userPermission: Array.isArray(decodedToken.permission) ? decodedToken.permission : decodedToken.permission ? [decodedToken.permission] as string[] : [] as string[],
                    userTimeZoneOffset: timeZoneOff,
                    userTimeZoneId: timeZoneId,
                    serverTimeZoneId: serverTimeZoneId,
                    updateTimeZoneOffset: updateTimeZoneOffset,
                    isProAptekaEnabled: isProAptekaEnabled,
                    /*, defaultViewListSettings: viewListSettings */
                }}>
                {props.children}
            </UserContext.Provider>
        )
    }
}

const useUserContext = () => useContext(UserContext)

export { useUserContext, UserContextProvider }